import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { Index } from 'react-virtualized';
import { RenderedRows } from 'react-virtualized/dist/es/List';
import { IGroup, IListRowRenderProps, ITeam } from '../../../../data/types';

import { List, ListSectionHeading } from '../../../shared/List/List';
import { ListItem, ListItemCell } from '../../../shared/List/ListItem';
import {
  groupByParentEntity,
  IGroupedEntityListItem,
} from '../../../shared/List/listUtils';
import { NoResults } from '../../../shared/NoResults';
import { ManageGroupPopup } from './ManageGroupPopup';

interface IProps extends RouteComponentProps {
  loading: boolean;
  groups: IGroup[];
  teams: ITeam[];
  selectedGroupId: string;
  onGroupChange: (id: string) => void;
  filterApplied: boolean;
  clearFilter: () => void;
  isMobile: boolean;
}

export const GroupList = withRouter((props: IProps): React.ReactElement => {
  const [heading, setHeading] = React.useState<string>();
  const [initialIndex, setInitialIndex] = React.useState<number>();

  /**
   * @description sets the initial index to scroll to on first render
   */
  React.useEffect((): void => {
    const selectedIndex = data.findIndex(
      (item: IGroupedEntityListItem): boolean => {
        return item.data.id === props.selectedGroupId;
      }
    );
    setInitialIndex(selectedIndex);
  }, [props.isMobile ? props.selectedGroupId : null]);

  /**
   * @description render function for list items
   * @param param0
   */
  const rowRenderer = ({
    index,
    key,
    style,
  }: IListRowRenderProps): React.ReactElement => {
    const row = data[index];
    if (row.isSticky) {
      return (
        <ListSectionHeading key={key} style={style} label={row.sectionLabel} />
      );
    } else {
      return (
        <GroupListItem
          style={style}
          group={row.data as IGroup}
          key={key}
          onClick={(): void => props.onGroupChange(row.data.id)}
          selected={props.selectedGroupId === row.data.id}
        />
      );
    }
  };

  /**
   * @description sets the current sticky header based on the group that is currently visible
   * @param param0
   */
  const onRowsRendered = ({ startIndex }: RenderedRows): void => {
    setHeading(data[startIndex].sectionLabel);
  };

  /**
   * @description creates a memoized list of items grouped by team that updates when props update
   */
  const data = React.useMemo(
    (): IGroupedEntityListItem[] =>
      groupByParentEntity(props.teams, props.groups, 'teams'),
    [props.groups, props.teams, props.loading, props.selectedGroupId]
  );

  const computeGroupedListRowHeight = (info: Index): number =>
    data[info.index]?.isSticky ? 30 : 50;

  return data.length ? (
    <List
      rowHeight={computeGroupedListRowHeight}
      rowRenderer={rowRenderer}
      listLength={data.length}
      onRowsRendered={onRowsRendered}
      stickyHeading={heading}
      scrollTo={initialIndex}
    />
  ) : (
    <NoResults
      message='No Groups Found'
      filterApplied={props.filterApplied}
      clearFilter={props.clearFilter}
    />
  );
});

interface IListItemProps {
  group: IGroup;
  onClick: (id: string) => void;
  style: React.CSSProperties;
  selected: boolean;
}

const GroupListItem = (props: IListItemProps): React.ReactElement => {
  const clickHandler = (): void => props.onClick(props.group.id);
  return (
    <ListItem
      onClick={clickHandler}
      style={props.style}
      selected={props.selected}
    >
      <ListItemCell style={{ minWidth: 0, overflow: 'hidden', flex: 2 }}>
        <span
          title={props.group.name}
          style={{
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
          }}
        >
          {props.group.name}
        </span>
      </ListItemCell>
      <ListItemCell>
        <ManageGroupPopup group={props.group} />
      </ListItemCell>
    </ListItem>
  );
};
